// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { useRecentTrips } from '../hooks/useRecentTrips';
import type { RecentTrip } from '../api/trip';
import SidebarSetting from './SidebarSetting';

type Props = {
  currentTripId: string;
  onShowAllHistory: () => void;
  onOpenUserChatRuleSettings: () => void;
  onNavigate: () => void;
};

const SidebarMenu = ({
  currentTripId,
  onShowAllHistory,
  onOpenUserChatRuleSettings,
  onNavigate
}: Props): JSX.Element => {
  const { theme } = useAITTheme();
  const history = useHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { trips, loading } = useRecentTrips(currentTripId);
  const styles = useMemo(() => getStyles(theme), [theme]);

  const isNewPage = currentTripId === 'new';
  const isActive = (tripId: string) => currentTripId === tripId;

  const handleNavigateToNew = () => {
    history.push('/trips/ai_messages/new');
    onNavigate();
  };

  const handleNavigateToTrip = (tripId: string) => {
    history.push(`/trips/${tripId}/ai_messages`);
    onNavigate();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
  };

  const truncateMessage = (message: string, maxLength = 10) => {
    if (!message) return '(メッセージなし)';
    if (message.length <= maxLength) return message;
    return `${message.slice(0, maxLength)}...`;
  };

  const getHistoryItemStyle = (trip: RecentTrip) => {
    if (isActive(trip.tripId)) {
      return styles.historyItemActive;
    }
    if (trip.draft) {
      return styles.historyItemDraft;
    }
    return styles.historyItem;
  };

  return (
    <div style={styles.container}>
      <button type="button" style={isNewPage ? styles.itemActive : styles.item} onClick={handleNavigateToNew}>
        新規チャット
      </button>
      <button type="button" style={styles.accordionHeader} onClick={() => setHistoryOpen(!historyOpen)}>
        <span>最近の履歴</span>
        <span style={styles.accordionIcon}>{historyOpen ? '▼' : '▶'}</span>
      </button>
      {historyOpen && (
        <div style={styles.accordionContent}>
          {loading ? (
            <div style={styles.statusText}>読み込み中...</div>
          ) : trips.length === 0 ? (
            <div style={styles.statusText}>履歴がありません</div>
          ) : (
            <>
              {trips.map(trip => (
                <button
                  key={trip.tripId}
                  type="button"
                  style={getHistoryItemStyle(trip)}
                  onClick={() => handleNavigateToTrip(trip.tripId)}
                >
                  <div style={styles.historyHeader}>
                    <div style={trip.draft ? styles.historyMessageDraft : styles.historyMessage}>
                      {truncateMessage(trip.lastMessage)}
                    </div>
                  </div>
                  <div style={styles.historyDate}>{formatDate(trip.createdAt)}</div>
                </button>
              ))}
              <button type="button" style={styles.viewAllLink} onClick={onShowAllHistory}>
                全ての履歴を見る
              </button>
            </>
          )}
        </div>
      )}
      <SidebarSetting onOpenUserChatRuleSettings={onOpenUserChatRuleSettings} />
    </div>
  );
};

const buttonReset = {
  background: 'none',
  border: 'none',
  textAlign: 'left' as const,
  display: 'block' as const,
  boxSizing: 'border-box' as const,
  font: 'inherit',
  outline: 'none',
  padding: 0,
  margin: 0
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: theme.contentBgColor,
    borderRight: `1px solid ${theme.grayBorderColor}`,
    minWidth: '200px',
    height: '100%',
    overflowY: 'auto' as const
  },
  item: {
    ...buttonReset,
    padding: '16px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor
  },
  itemActive: {
    ...buttonReset,
    padding: '16px 24px',
    marginTop: '16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    backgroundColor: '#e6f4fb',
    fontWeight: 600 as const,
    borderLeft: `4px solid ${theme.linkColor}`
  },
  accordionHeader: {
    ...buttonReset,
    padding: '16px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  accordionIcon: {
    fontSize: '10px',
    color: theme.grayTextColor
  },
  accordionContent: {
    backgroundColor: theme.whiteColor
  },
  historyItem: {
    ...buttonReset,
    width: '100%',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#f5f5f5',
    color: theme.grayTextColor
  },
  historyItemDraft: {
    ...buttonReset,
    width: '100%',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#e6f4fb'
  },
  historyItemActive: {
    ...buttonReset,
    width: '100%',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#d0ebf7',
    borderLeft: `4px solid ${theme.linkColor}`
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px'
  },
  historyMessage: {
    color: theme.grayTextColor,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1
  },
  historyMessageDraft: {
    color: theme.textColor,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1
  },
  historyDate: {
    fontSize: '11px',
    color: theme.grayTextColor,
    marginTop: '4px'
  },
  viewAllLink: {
    ...buttonReset,
    width: '100%',
    padding: '12px 24px',
    fontSize: '12px',
    color: theme.textColor,
    textAlign: 'center' as const,
    cursor: 'pointer'
  },
  statusText: {
    padding: '12px 24px',
    fontSize: '13px',
    color: theme.grayTextColor
  }
});

export default SidebarMenu;
